import SEOHead from './SEOHead';
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Calendar, MapPin, Clock, User, IndianRupee, Users, Sparkles, ArrowLeft, CheckCircle2 } from 'lucide-react';
import Footer from './Footer';
import JsonLd from './JsonLd';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const EventDetails = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { user, openAuth } = useContext(AuthContext);
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { fetchEvent(); /* eslint-disable-next-line */ }, [eventId]);

  const fetchEvent = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/events/${eventId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setEvent(res.data.event);
    } catch (e) {
      toast.error('Failed to load event');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return 'TBA';
    return new Date(iso).toLocaleString('en-IN', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const handleRegister = async () => {
    if (!user) {
      toast.info('Please sign in to register');
      openAuth('login');
      return;
    }
    if (event.event_type === 'free') {
      setSubmitting(true);
      try {
        await axios.post(`${API}/api/events/${event.id}/register-free`, {}, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        toast.success('You\'re registered! 🎉');
        fetchEvent();
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Registration failed');
      } finally {
        setSubmitting(false);
      }
      return;
    }
    // Paid event → Razorpay flow
    navigate(`/event-checkout/${event.id}`);
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-gray-500">Loading event…</div>;
  }
  if (!event) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 p-4 text-center">
        <Sparkles className="h-10 w-10 text-gray-300" />
        <p className="text-gray-700 font-medium">Event not found</p>
        <Button onClick={() => navigate('/events')} variant="outline">Browse events</Button>
      </div>
    );
  }

  const base = event.fee || 0;
  const gst = Math.round(base * 0.18);
  const total = base + gst;

  // ───────── JSON-LD: Event schema for Google rich results ─────────
  const stripHtml = (html) => (html || '').replace(/<[^>]*>/g, '').trim();
  const isOnline = !!event.online_link || (event.venue || '').toLowerCase() === 'online';
  const eventSchema = {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: event.title,
    description: stripHtml(event.description) || event.title,
    ...(event.event_date ? { startDate: event.event_date } : {}),
    eventAttendanceMode: isOnline
      ? 'https://schema.org/OnlineEventAttendanceMode'
      : 'https://schema.org/OfflineEventAttendanceMode',
    eventStatus: 'https://schema.org/EventScheduled',
    location: isOnline
      ? {
          '@type': 'VirtualLocation',
          url: event.online_link || `https://nerdnation.world/events/${eventId}`,
        }
      : {
          '@type': 'Place',
          name: event.venue || 'Nerd Nation Event',
          address: {
            '@type': 'PostalAddress',
            addressLocality: event.venue || 'India',
            addressCountry: 'IN',
          },
        },
    ...(event.banner_url && !event.banner_url.startsWith('data:')
      ? { image: [event.banner_url] }
      : {}),
    organizer: {
      '@type': 'Organization',
      name: event.speaker_name || 'Nerd Nation',
      url: 'https://nerdnation.world',
    },
    ...(event.event_type === 'paid' && total > 0
      ? {
          offers: {
            '@type': 'Offer',
            price: total,
            priceCurrency: 'INR',
            availability: 'https://schema.org/InStock',
            url: `https://nerdnation.world/events/${eventId}`,
            validFrom: event.created_at,
          },
        }
      : {
          offers: {
            '@type': 'Offer',
            price: 0,
            priceCurrency: 'INR',
            availability: 'https://schema.org/InStock',
            url: `https://nerdnation.world/events/${eventId}`,
          },
        }),
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-white to-amber-50/30">
      <SEOHead title={event.title} description={event.description?.slice(0, 160) || 'Event on Nerd Nation'} path={`/events/${eventId}`} ogType="event" />
      <JsonLd data={eventSchema} />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
        <button onClick={() => navigate('/events')} className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 mb-4">
          <ArrowLeft className="h-4 w-4" /> Back to events
        </button>

        {/* Banner */}
        <div className="relative aspect-[16/7] rounded-2xl overflow-hidden bg-gradient-to-br from-orange-100 to-amber-50 mb-6">
          {event.banner_url ? (
            <img src={event.banner_url} alt={event.title} className="w-full h-full object-cover" loading="lazy" decoding="async"/>
          ) : (
            <div className="flex items-center justify-center h-full"><Sparkles className="h-16 w-16 text-orange-300" /></div>
          )}
          <Badge className={`absolute top-4 right-4 ${event.event_type === 'free' ? 'bg-green-500' : 'bg-blue-600'} text-white border-0 text-sm px-3 py-1`}>
            {event.event_type === 'free' ? 'FREE' : `₹${total.toLocaleString('en-IN')}`}
          </Badge>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            {event.category && <Badge variant="outline">{event.category}</Badge>}
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{event.title}</h1>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-gray-700">
              {event.event_date && (<div className="flex items-center gap-2"><Calendar className="h-4 w-4 text-orange-500" />{formatDate(event.event_date)}</div>)}
              {event.duration && (<div className="flex items-center gap-2"><Clock className="h-4 w-4 text-orange-500" />{event.duration}</div>)}
              {(event.venue || event.online_link) && (<div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-orange-500" />{event.venue || 'Online'}</div>)}
              {event.speaker_name && (<div className="flex items-center gap-2"><User className="h-4 w-4 text-orange-500" />{event.speaker_name}</div>)}
              {event.capacity && (<div className="flex items-center gap-2"><Users className="h-4 w-4 text-orange-500" />{event.registrations_count}/{event.capacity} registered</div>)}
            </div>
            <Card>
              <CardContent className="p-4 sm:p-6">
                <h2 className="font-semibold text-gray-900 mb-2">About this event</h2>
                <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap">{event.description}</div>
              </CardContent>
            </Card>
            {event.agenda && (
              <Card>
                <CardContent className="p-4 sm:p-6">
                  <h2 className="font-semibold text-gray-900 mb-2">Agenda</h2>
                  <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap">{event.agenda}</div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sticky register card */}
          <div className="lg:sticky lg:top-4 h-fit">
            <Card className="border-2 border-orange-200 shadow-lg">
              <CardContent className="p-5 space-y-4">
                {event.event_type === 'paid' ? (
                  <>
                    <div className="text-sm text-gray-600">Registration fee</div>
                    <div>
                      <div className="flex items-center gap-1 text-3xl font-bold text-gray-900">
                        <IndianRupee className="h-5 w-5" />{total.toLocaleString('en-IN')}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        Base ₹{base.toLocaleString('en-IN')} + 18% GST ₹{gst.toLocaleString('en-IN')}
                      </div>
                    </div>
                  </>
                ) : (
                  <div>
                    <div className="text-3xl font-bold text-green-600">FREE</div>
                    <div className="text-xs text-gray-500 mt-1">No payment required</div>
                  </div>
                )}

                {event.is_registered ? (
                  <Button disabled className="w-full bg-green-600 hover:bg-green-600 text-white" data-testid="event-registered-btn">
                    <CheckCircle2 className="h-4 w-4 mr-2" /> You're registered
                  </Button>
                ) : (
                  <Button
                    data-testid="event-register-btn"
                    onClick={handleRegister}
                    disabled={submitting || event.status !== 'active'}
                    className="w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white font-semibold"
                  >
                    {submitting ? 'Registering…' : (event.event_type === 'free' ? 'Register Now' : `Register Now — ₹${total.toLocaleString('en-IN')}`)}
                  </Button>
                )}
                <p className="text-[11px] text-gray-500 text-center">
                  {event.event_type === 'paid' ? 'Secure payment via Razorpay' : 'Secure your seat instantly'}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default EventDetails;
